import React from 'react'
import { useQuery } from 'react-relay-offline'
import { graphql } from 'babel-plugin-relay/macro'
import { Skeleton, Text, useBreakpoint, csx } from '@vtex/admin-ui'
import { FormattedMessage, useIntl } from 'react-intl'
import { useHistory, useParams } from 'react-router'

import {
  IndicatorCards,
  useIndicatorsCardsState,
} from './components/IndicatorCards'
import { useIsMounted } from '../../../../core/hooks'
import {
  TopbarLeftArea,
  TopbarRightArea,
} from '../../../Instore/components/Header/Topbar'
import { CloseButton } from '../../../Checkout/components/CloseButton'
import type { SalesAssociatePerformanceScreenQuery } from './__generated__/SalesAssociatePerformanceScreenQuery.graphql'
import { useDrawer } from '../../../../ui/components/Drawer/hooks'
import { ReadableMessageError } from '../../../../core/screens/GenericError/ReadableMessageError'
import { SalesPeriod } from './components/SalesPeriod/SalesPeriod'
import { useSalesPeriodPicker } from './components/SalesPeriod/SalesPeriodPicker'
import { SalesPeriodButton } from './components/SalesPeriod/SalesPeriodButton'
import { SalesPeriodDrawer } from './components/SalesPeriod/SalesPeriodDrawer'

type SalesAssociatePerformanceScreenParams = {
  salesAssociateId: string
  storeId: string
}

export const SalesAssociatePerformanceScreen = () => {
  const {
    salesAssociateId,
    storeId,
  } = useParams<SalesAssociatePerformanceScreenParams>()

  const intl = useIntl()
  const { breakpoint } = useBreakpoint()
  const isMounted = useIsMounted()
  const history = useHistory()

  const salesPeriodDrawer = useDrawer({ route: '#/sales-period-drawer' })
  const [period, setPeriod] = useSalesPeriodPicker()

  const {
    data,
    isLoading,
    error,
  } = useQuery<SalesAssociatePerformanceScreenQuery>(
    graphql`
      query SalesAssociatePerformanceScreenQuery(
        $storeId: ID!
        $salesAssociateId: ID!
        $period: SalesPeriod!
      ) {
        salesAssociate: node(id: $salesAssociateId) {
          ... on SalesAssociate {
            name
            __typename
          }
        }
        viewer {
          configuration {
            viewOthersSalesAssociatesPerformance
          }
          salesAssociate {
            id
          }
        }
        ...IndicatorCards_indicators
      }
    `,
    {
      storeId,
      salesAssociateId,
      period: SalesPeriod.toSalesPeriodInput(period),
    },
    {
      fetchPolicy: 'store-and-network',
      networkCacheConfig: {
        ttl: 7 * 24 * 60 * 60 * 1000 /* 7 days */,
      },
    }
  )

  const indicators = useIndicatorsCardsState({ indicators: data, isLoading })

  const { salesAssociate } = data ?? {}

  if (error) {
    throw error
  }

  if (!data && !isLoading) {
    throw new Error('SalesAssociatePerformanceScreenQuery returned empty data')
  }

  if (salesAssociate && salesAssociate.__typename !== 'SalesAssociate') {
    throw new Error(`Invalid sales associate id: ${salesAssociateId}`)
  }

  if (
    data &&
    data.viewer.configuration.viewOthersSalesAssociatesPerformance === false &&
    data.viewer.salesAssociate?.id !== salesAssociateId
  ) {
    throw new ReadableMessageError(
      'cannot-view-other-sales-associates-performance'
    )
  }

  const handleCloseButtonClick = () => {
    history.goBack()
  }

  return (
    <>
      <TopbarLeftArea>
        <Text variant="title2">
          <FormattedMessage id="menu-sales-performance" />
        </Text>
      </TopbarLeftArea>
      <TopbarRightArea>
        <div className={csx({ display: 'flex', float: 'right' })}>
          <CloseButton onClick={handleCloseButtonClick} />
        </div>
      </TopbarRightArea>
      <div
        data-testid="sales-performance-container"
        className={csx({
          paddingBottom: '$space-8',
          paddingTop:
            breakpoint === 'mobile' || breakpoint === 'tablet'
              ? '$space-4'
              : '80px',
        })}
      >
        {/* TODO: arrumar de -> do/a dependendo do sexo? */}
        <div className={csx({ display: 'flex', flexDirection: 'column' })}>
          <div
            className={csx({
              display: 'flex',
              flexDirection: 'column',
              '@tablet': {
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'end',
              },
            })}
          >
            <Text
              variant="display"
              className={csx({
                marginBottom: '$space-6',
              })}
            >
              <FormattedMessage
                id="sales-performance.sales-made-by"
                values={{
                  salesAssociateName: (
                    <span
                      className={csx({
                        color: '$blue40',
                        fontSize: '1.5rem',
                      })}
                    >
                      {salesAssociate ? salesAssociate.name : <Skeleton />}
                    </span>
                  ),
                }}
              />
            </Text>
            <div
              className={csx({
                display: 'flex',
                flexDirection: 'column',
              })}
            >
              <Text
                variant="detail"
                className={csx({
                  display: 'block',
                  color: '$disabled',
                  marginBottom: '$space-2',
                })}
              >
                <FormattedMessage id="sales-performance.sales-period.show" />
              </Text>
              <SalesPeriodButton
                value={period}
                label={intl.formatMessage({
                  id: 'sales-performance.my-sales',
                })}
                onClick={() => {
                  if (!isMounted) return
                  salesPeriodDrawer.open()
                }}
                className={csx({ marginBottom: '$space-8' })}
              />
            </div>
          </div>

          <IndicatorCards state={indicators} />
        </div>
      </div>
      <SalesPeriodDrawer
        initialValue={period}
        onChange={(value) => setPeriod(value)}
        onClose={() => salesPeriodDrawer.close()}
        isOpen={salesPeriodDrawer.isOpen}
        data-testid="sales-performance__sales-period-drawer"
      />
    </>
  )
}
